import * as path from "node:path";
import * as fs from "node:fs";
import { createHash, randomBytes } from "node:crypto";
import type { TokenCredential } from "@azure/identity";
import { classifyFoundryError } from "@m8t-stack/foundry-invoke";
import {
  mintInstallationToken,
} from "@m8t-stack/github-app-auth";
import {
  BRAIN_MCP_BEARER_HASH_METADATA_KEY,
  GATEWAY_BRAIN_MCP_TRANSPORT,
  parseBrainLink,
  serializeBrainLink,
  type BrainLink,
  type BrainTransport,
} from "@m8t-stack/api-contract";
import { LocalCliError } from "./errors.js";
import { readAgentYaml, patchAgentYaml, writeAgentYaml, type BrainBlock } from "./agent-yaml.js";
import { renderPersonaBody } from "./persona-render.js";
import { appendBrainLoader } from "./brain-loader-block.js";
import { hasA2aSnippet, appendSnippet as appendA2aSnippet } from "./a2a-snippet.js";
import { getAgentVersion, type AgentDefinition } from "./foundry-agent-get.js";
import { createAgentVersion } from "./foundry-agent-version.js";
import { mirrorBrainYaml } from "./brain-yaml-mirror.js";
import type { FoundryProject } from "./foundry-project.js";

const FOUNDRY_ARM_API = "2025-04-01-preview";
const ARM_SCOPE = "https://management.azure.com/.default";
const LEGACY_GITHUB_MCP_TOOL_NAMES = [
  "get_file_contents",
  "push_files",
  "search_code",
  "search_repositories",
] as const;
export const BRAIN_MCP_TOOL_NAMES = [
  "brain_read_indexes",
  "brain_read_file",
  "brain_write_memory",
  "brain_write_scratch",
  "brain_retract_memory",
] as const;
const GITHUB_MCP_SERVER_URL = "https://api.githubcopilot.com/mcp";

export interface LinkBrainArgs {
  credential: TokenCredential;
  kvUri: string;
  projectEndpoint: string;       // data-plane URL
  projectArmId: string;          // /subscriptions/.../projects/<proj>
  agentName: string;
  repo: string;                  // "owner/name"
  branch: string;
  gatewayMcpUrl?: string;        // <gateway-base>/api/brain-agent/mcp (prompt gateway mode)
  legacyGithubMcp?: boolean;     // explicit rollback mode for prompt agents
  repoRoot: string;              // path to m8t repo (for brain-loader.md)
  home?: string;                 // for testing — defaults to os.homedir()
  installationId?: string;       // pre-known id (e.g. from CLI poll); when absent, caller polls
  skipIfLinked?: boolean;        // if true, skip createVersion + brain.yaml write when already linked
  onProgress?: (msg: string) => void;
  // Hosted-agent fields — required when the agent has kind:"hosted"
  subscriptionId?: string;
  project?: FoundryProject;
  modelDeployment?: string;
  allowNonReasoning?: boolean;
  personaPathOverride?: string;  // --persona: re-render from this file instead of the yaml's personaPath
}

export interface LinkBrainResult {
  installationId: string;
  connectionName: string;
  foundryVersion?: string;
  alreadyLinked: boolean;
}

/**
 * The convergent link step.
 *
 * Caller (CLI command) has already:
 *   - Verified agent exists (or created the repo, for `m8t brain create`)
 *   - Done the App-install poll (or expects to via this function's
 *     installationId arg if pre-known)
 *   - Run the inline `checkAppHealth` precondition
 *
 * This function:
 *   1. Reads current agent definition (to clone instructions + tools)
 *   2. Reads per-agent yaml for personaPath + fillableFieldValues
 *   3. Re-renders persona body + appends brain loader (templated with repo + branch)
 *   4. Provisions the prompt agent's MCP connection. New links use a random,
 *      non-rotating gateway bearer; explicit legacy links use a GitHub App token.
 *   5. Composes + POSTs createVersion with brain tool + metadata.brain
 *   6. Writes .m8t/brain.yaml mirror to the repo
 *   7. Patches local per-agent yaml with the brain block
 *
 * Idempotent: when `skipIfLinked=true` AND metadata.brain already matches
 * args, gateway links are a complete no-op. Legacy GitHub links still refresh
 * their expiring installation token but skip steps 5–7.
 */
export async function linkBrain(args: LinkBrainArgs): Promise<LinkBrainResult> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- noop progress stub
  const progress = args.onProgress ?? ((_m: string) => {});
  const baseConnectionName = `brain-${args.agentName}`;

  if (!validRepository(args.repo) || !validBranch(args.branch) || !validConnectionName(baseConnectionName)) {
    throw new LocalCliError({
      code: "LINK_INVALID_TARGET",
      message: "Brain repository, branch, or agent name is not safe to link.",
    });
  }

  if (!args.installationId) {
    throw new LocalCliError({
      code: "LINK_NO_INSTALLATION_ID",
      message: "linkBrain requires installationId. CLI command must poll for it before calling.",
    });
  }

  progress("Reading current agent definition…");
  const current = await getAgentVersion({
    credential: args.credential,
    projectEndpoint: args.projectEndpoint,
    agentName: args.agentName,
  });

  // Kind-aware: hosted agents bypass the MCP attachment + Foundry connection
  // entirely — the container self-mints and reads/writes via the GitHub API.
  if (current.definition.kind === "hosted") {
    if (!args.project || !args.subscriptionId) {
      throw new LocalCliError({
        code: "HOSTED_LINK_MISSING_CONTEXT",
        message: "linkBrain: hosted agents require project + subscriptionId (the CLI command must resolve them).",
      });
    }
    progress("hosted agent — enabling in-container brain access…");
    const { enableHostedBrain } = await import("./enable-hosted-brain.js");
    const res = await enableHostedBrain({
      credential: args.credential,
      subscriptionId: args.subscriptionId,
      project: args.project,
      kvUri: args.kvUri,
      agentName: args.agentName,
      repo: args.repo,
      branch: args.branch,
      installationId: args.installationId,
      modelDeployment: args.modelDeployment,
      allowNonReasoning: args.allowNonReasoning,
      onProgress: args.onProgress,
    });
    return {
      installationId: args.installationId,
      connectionName: "in-container",
      foundryVersion: res.version,
      alreadyLinked: res.alreadyLinked,
    };
  }

  const transport: BrainTransport = args.legacyGithubMcp === true
    ? "github-mcp"
    : GATEWAY_BRAIN_MCP_TRANSPORT;

  // Idempotency check includes branch + transport. Changing either must create
  // a new version so the MCP server and loader agree with metadata.brain.
  const currentLink = parseExistingBrain(current.metadata?.brain);
  const alreadyLinked = !!(
    args.skipIfLinked &&
    currentLink?.repo === args.repo &&
    currentLink.branch === args.branch &&
    currentLink.topology === "per-worker" &&
    currentLink.schemaVersion === "1" &&
    currentLink.installationId === args.installationId &&
    validConnectionName(currentLink.credentialRef) &&
    currentLink.transport === transport &&
    (transport === "github-mcp" ||
      /^[a-f0-9]{64}$/.test(current.metadata?.[BRAIN_MCP_BEARER_HASH_METADATA_KEY] ?? ""))
  );

  // A relink never mutates the connection used by the currently active agent
  // version. Provision a generation-specific sibling first, switch the agent
  // version to it, then retire the old connection. If version creation fails,
  // the old version and its bearer remain usable.
  const connectionName = alreadyLinked
    ? currentLink.credentialRef
    : currentLink
      ? nextConnectionName(baseConnectionName)
      : baseConnectionName;

  let brainMcpBearerHash = current.metadata?.[BRAIN_MCP_BEARER_HASH_METADATA_KEY];
  if (transport === "github-mcp") {
    progress("Refreshing App installation token in legacy GitHub MCP connection…");
    const minted = await mintInstallationToken({
      credential: args.credential,
      kvUri: args.kvUri,
      installationId: args.installationId,
      repository: args.repo,
    });
    await putLegacyGithubMcpConnection({
      credential: args.credential,
      projectArmId: args.projectArmId,
      connectionName,
      bearer: minted.token,
    });
    progress(`Token refreshed (expires ${minted.expiresAt.toISOString()})`);
    brainMcpBearerHash = "";
  } else if (!alreadyLinked) {
    if (!args.gatewayMcpUrl) {
      throw new LocalCliError({
        code: "LINK_NO_GATEWAY_URL",
        message: "linkBrain requires gatewayMcpUrl for gateway-mcp-v1 prompt links.",
      });
    }
    const bearer = randomBytes(32).toString("base64url");
    brainMcpBearerHash = createHash("sha256").update(bearer).digest("hex");
    progress("Provisioning narrow brain MCP gateway connection…");
    await putGatewayMcpConnection({
      credential: args.credential,
      projectArmId: args.projectArmId,
      connectionName,
      gatewayMcpUrl: args.gatewayMcpUrl,
      bearer,
    });
  }

  let foundryVersion: string | undefined;

  if (!alreadyLinked) {
    const serverUrl = transport === GATEWAY_BRAIN_MCP_TRANSPORT
      ? args.gatewayMcpUrl
      : GITHUB_MCP_SERVER_URL;
    if (!serverUrl) {
      throw new LocalCliError({
        code: "LINK_NO_GATEWAY_URL",
        message: "linkBrain requires gatewayMcpUrl for gateway-mcp-v1 prompt links.",
      });
    }
    const loaderPath = path.join(args.repoRoot, "targets/foundry/brain-loader.md");
    const loaderTemplate = fs.readFileSync(loaderPath, "utf8");
    const loader = renderLoaderForTransport(loaderTemplate, transport)
      .replaceAll("{{brain_repo}}", args.repo)
      .replaceAll("{{brain_branch}}", args.branch);

    const yaml = readAgentYaml(args.agentName, args.home);
    const personaPath = args.personaPathOverride ?? yaml?.personaPath;

    let base: string;
    if (personaPath) {
      progress("Re-rendering persona body with brain loader…");
      const resolved = personaPath.startsWith("/") ? personaPath : path.join(args.repoRoot, personaPath);
      base = renderPersonaBody(resolved, yaml?.fillableFieldValues ?? {}, args.agentName);
    } else {
      // Fallback (no local yaml — e.g. a REST/SDK-created agent): base on the
      // deployed instructions; appendBrainLoader strips any prior loader first.
      progress("No local persona yaml — basing instructions on the deployed agent…");
      base = current.definition.instructions ?? "";
    }
    let instructionsWithLoader = appendBrainLoader(base, loader);
    // Relink-safety: the persona re-render above discards the live instructions,
    // which carry the a2a delegate snippet (enableA2a appends it post-deploy, not
    // in the persona file). Re-attach it when the deployed agent already had it so
    // --force never silently strips the "Working with other workers" guidance. The
    // a2a *tool* is preserved separately (cloned in createBrainEnabledVersion).
    // appendA2aSnippet strips-then-appends, so this is idempotent and safe even in
    // the fallback branch where `base` already inlines the snippet (no duplication).
    if (hasA2aSnippet(current.definition.instructions ?? "")) {
      instructionsWithLoader = appendA2aSnippet(instructionsWithLoader);
    }

    const link: BrainLink = {
      repo: args.repo,
      branch: args.branch,
      topology: "per-worker",
      schemaVersion: "1",
      credentialRef: connectionName,
      installationId: args.installationId,
      transport,
    };

    progress("Deploying brain-enabled agent version…");
    foundryVersion = await createBrainEnabledVersion({
      credential: args.credential,
      projectEndpoint: args.projectEndpoint,
      agentName: args.agentName,
      currentDefinition: current.definition,
      currentMetadata: current.metadata ?? {},
      instructionsWithLoader,
      connectionName,
      serverUrl,
      allowedTools: transport === "gateway-mcp-v1"
        ? BRAIN_MCP_TOOL_NAMES
        : LEGACY_GITHUB_MCP_TOOL_NAMES,
      brainLinkJson: serializeBrainLink(link),
      brainMcpBearerHash: brainMcpBearerHash ?? "",
    });

    if (
      currentLink?.credentialRef &&
      currentLink.credentialRef !== connectionName &&
      validConnectionName(currentLink.credentialRef)
    ) {
      try {
        await deleteFoundryConnection({
          credential: args.credential,
          projectArmId: args.projectArmId,
          connectionName: currentLink.credentialRef,
        });
      } catch (error) {
        progress(`Warning: the previous Brain connection could not be retired: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    progress("Mirroring .m8t/brain.yaml to brain repo…");
    await mirrorBrainYaml({
      credential: args.credential,
      kvUri: args.kvUri,
      installationId: args.installationId,
      repo: args.repo,
      branch: args.branch,
      persona: yaml?.personaVersion
        ? `${current.metadata?.persona ?? args.agentName}@${yaml.personaVersion}`
        : current.metadata?.persona ?? args.agentName,
      agent: args.agentName,
    });

    progress("Recording the brain link in the per-agent yaml…");
    const brainBlock: BrainBlock = {
      repo: args.repo,
      branch: args.branch,
      installationId: args.installationId,
      credentialRef: connectionName,
      linkedAt: new Date().toISOString(),
    };
    if (yaml) {
      patchAgentYaml(args.agentName, { brain: brainBlock }, args.home);
    } else {
      // Synthesize a minimal yaml so brain unlink / agent remove / future ops work.
      writeAgentYaml(
        args.agentName,
        {
          agentName: args.agentName,
          agentId: "",
          projectEndpoint: args.projectEndpoint,
          // model may be empty for an SDK-created agent without a deployed model field
          model: typeof current.definition.model === "string" ? current.definition.model : "",
          personaVersion: current.metadata?.personaVersion ?? null,
          personaPath: "",
          deployedAt: new Date().toISOString(),
          playgroundUrl: "",
          environments: { default: { projectEndpoint: args.projectEndpoint } },
          brain: brainBlock,
        },
        args.home,
      );
    }
  }

  return {
    installationId: args.installationId,
    connectionName,
    foundryVersion,
    alreadyLinked,
  };
}

// ──── helpers ───────────────────────────────────────────────────────────────

function parseExistingBrain(raw: string | undefined): BrainLink | undefined {
  return parseBrainLink(raw ? { brain: raw } : undefined);
}

function validConnectionName(value: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(value);
}

function validRepository(value: string): boolean {
  const parts = value.split("/");
  return parts.length === 2 && parts.every((part) =>
    /^[A-Za-z0-9][A-Za-z0-9_.-]*$/.test(part) &&
    part !== "." && part !== ".." && !part.endsWith(".lock"));
}

function validBranch(value: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(value) &&
    !value.includes("..") && !value.includes("//") && !value.includes("@{") &&
    !value.endsWith("/") && !value.endsWith(".") && !value.endsWith(".lock");
}

function nextConnectionName(base: string): string {
  const suffix = randomBytes(6).toString("hex");
  return `${base.slice(0, 115)}-${suffix}`;
}

async function deleteFoundryConnection(args: {
  credential: TokenCredential;
  projectArmId: string;
  connectionName: string;
}): Promise<void> {
  const armToken = await args.credential.getToken(ARM_SCOPE);
  if (!armToken?.token) throw new Error("Could not acquire ARM token for old Brain connection cleanup");
  const url = `https://management.azure.com${args.projectArmId}/connections/${args.connectionName}?api-version=${FOUNDRY_ARM_API}`;
  const response = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${armToken.token}` },
  });
  if (!response.ok && response.status !== 404) {
    throw new Error(`DELETE ${url}: HTTP ${String(response.status)}`);
  }
}

function renderLoaderForTransport(template: string, transport: BrainTransport): string {
  return transport === GATEWAY_BRAIN_MCP_TRANSPORT
    ? unwrapTemplateBlock(stripTemplateBlock(template, "legacy-github-mcp"), "gateway-mcp-v1")
    : unwrapTemplateBlock(stripTemplateBlock(template, "gateway-mcp-v1"), "legacy-github-mcp");
}

function stripTemplateBlock(template: string, name: string): string {
  const start = `<!-- m8t:${name}:start -->`;
  const end = `<!-- m8t:${name}:end -->`;
  const startAt = template.indexOf(start);
  if (startAt < 0) return template;
  const endAt = template.indexOf(end, startAt + start.length);
  if (endAt < 0) return template;
  return `${template.slice(0, startAt)}${template.slice(endAt + end.length)}`.trimEnd();
}

function unwrapTemplateBlock(template: string, name: string): string {
  return template
    .replace(`<!-- m8t:${name}:start -->`, "")
    .replace(`<!-- m8t:${name}:end -->`, "")
    .trim();
}

async function putLegacyGithubMcpConnection(args: {
  credential: TokenCredential;
  projectArmId: string;
  connectionName: string;
  bearer: string;
}): Promise<void> {
  // PUT converges both the target and the credential shape. This matters for an
  // explicit rollback from gateway-mcp-v1, where the connection already exists
  // but points at the gateway rather than GitHub's MCP server.
  await putFoundryCustomKeysConnection({
    credential: args.credential,
    projectArmId: args.projectArmId,
    connectionName: args.connectionName,
    properties: {
      authType: "CustomKeys", category: "CustomKeys",
      target: GITHUB_MCP_SERVER_URL,
      isSharedToAll: false,
      credentials: { keys: { Authorization: `Bearer ${args.bearer}` } },
      metadata: { managedBy: "m8t-brain-link", transport: "github-mcp" },
    },
  });
}

async function putGatewayMcpConnection(args: {
  credential: TokenCredential;
  projectArmId: string;
  connectionName: string;
  gatewayMcpUrl: string;
  bearer: string;
}): Promise<void> {
  await putFoundryCustomKeysConnection({
    credential: args.credential,
    projectArmId: args.projectArmId,
    connectionName: args.connectionName,
    properties: {
      authType: "CustomKeys",
      category: "CustomKeys",
      target: args.gatewayMcpUrl,
      isSharedToAll: false,
      credentials: { keys: { Authorization: `Bearer ${args.bearer}` } },
      metadata: { managedBy: "m8t-brain-link", transport: "gateway-mcp-v1" },
    },
  });
}

interface IntendedCustomKeysConnection {
  authType: "CustomKeys";
  category: "CustomKeys";
  target: string;
  isSharedToAll: false;
  credentials: { keys: { Authorization: string } };
  metadata: Record<string, string>;
}

/**
 * Azure's connection RP has returned a 500 after durably creating the exact
 * resource. A blind retry would mint a different gateway bearer while the
 * first connection remains live. For an ambiguous, retryable failure only,
 * verify the exact resource and continue this same in-memory attempt.
 *
 * Connection credentials are write-only, so GET cannot prove their value.
 * Every non-secret field we submitted is checked instead; the caller retains
 * the same bearer and derives the agent metadata hash from that same value.
 */
async function putFoundryCustomKeysConnection(args: {
  credential: TokenCredential;
  projectArmId: string;
  connectionName: string;
  properties: IntendedCustomKeysConnection;
}): Promise<void> {
  const armToken = await args.credential.getToken(ARM_SCOPE);
  if (!armToken?.token) {
    throw new LocalCliError({ code: "ARM_AUTH", message: "Could not acquire ARM token" });
  }
  const url = `https://management.azure.com${args.projectArmId}/connections/${args.connectionName}?api-version=${FOUNDRY_ARM_API}`;
  const headers = { Authorization: `Bearer ${armToken.token}`, "Content-Type": "application/json" };
  const intended: IntendedCustomKeysConnection = {
    ...args.properties,
    metadata: {
      ...args.properties.metadata,
      // The other non-secret fields can match a stale connection from an older
      // attempt whose write-only bearer is different. This nonce proves the GET
      // observed this exact PUT, without exposing or attempting to read the key.
      provisioningNonce: randomBytes(32).toString("base64url"),
    },
  };
  let res: Response;
  try {
    res = await fetch(url, { method: "PUT", headers, body: JSON.stringify({ properties: intended }) });
  } catch (error) {
    if (
      isAmbiguousConnectionPutError(error) &&
      await exactConnectionExists({ url, authorization: headers.Authorization, intended })
    ) return;
    throw error;
  }
  if (!res.ok) {
    const text = await res.text().catch((error: unknown) =>
      `<response body unavailable: ${error instanceof Error ? error.message : String(error)}>`);
    const originalFailure = new LocalCliError({
      code: "CONN_CREATE_FAILED",
      message: `PUT ${url}: HTTP ${res.status.toString()}\n${text.slice(0, 300)}`,
    });
    if (
      isAmbiguousConnectionPutStatus(res.status) &&
      await exactConnectionExists({ url, authorization: headers.Authorization, intended })
    ) return;
    throw originalFailure;
  }
}

function isAmbiguousConnectionPutStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

function isAmbiguousConnectionPutError(error: unknown): boolean {
  let current: unknown = error;
  const seen = new Set<unknown>();
  for (let depth = 0; depth < 5 && current !== undefined && !seen.has(current); depth++) {
    seen.add(current);
    const classified = classifyFoundryError(current);
    if (classified.retryable &&
      (classified.category === "transient" || classified.category === "rate_limit")) return true;
    current = isRecord(current) ? current.cause : undefined;
  }
  return false;
}

async function exactConnectionExists(args: {
  url: string;
  authorization: string;
  intended: IntendedCustomKeysConnection;
}): Promise<boolean> {
  try {
    const res = await fetch(args.url, {
      method: "GET",
      headers: { Authorization: args.authorization },
    });
    if (!res.ok) return false;
    const resource: unknown = await res.json();
    if (!isRecord(resource) || !isRecord(resource.properties)) return false;
    const { credentials: _writeOnlyCredentials, ...nonSecretIntended } = args.intended;
    return containsIntendedShape(resource.properties, nonSecretIntended);
  } catch {
    return false;
  }
}

function containsIntendedShape(actual: unknown, intended: unknown): boolean {
  if (!isRecord(intended)) return Object.is(actual, intended);
  if (!isRecord(actual)) return false;
  return Object.entries(intended).every(([key, value]) =>
    Object.hasOwn(actual, key) && containsIntendedShape(actual[key], value));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function createBrainEnabledVersion(args: {
  credential: TokenCredential;
  projectEndpoint: string;
  agentName: string;
  currentDefinition: AgentDefinition;
  currentMetadata: Record<string, string>;
  instructionsWithLoader: string;
  connectionName: string;
  serverUrl: string;
  allowedTools: readonly string[];
  brainLinkJson: string;
  brainMcpBearerHash: string;
}): Promise<string> {
  // Strip any existing brain tool, then append the canonical one.
  const otherTools = (args.currentDefinition.tools ?? []).filter(
    (t) => !(t.type === "mcp" && (t as { server_label?: string }).server_label === "brain"),
  );
  const brainTool = {
    type: "mcp" as const,
    server_label: "brain",
    server_url: args.serverUrl,
    allowed_tools: args.allowedTools,
    require_approval: "never" as const,
    project_connection_id: args.connectionName,
  };
  const definition: AgentDefinition = {
    ...args.currentDefinition,
    instructions: args.instructionsWithLoader,
    tools: [...otherTools, brainTool],
  };
  const metadata: Record<string, string> = {
    ...args.currentMetadata,
    brain: args.brainLinkJson,
    [BRAIN_MCP_BEARER_HASH_METADATA_KEY]: args.brainMcpBearerHash,
  };
  return createAgentVersion({
    credential: args.credential,
    projectEndpoint: args.projectEndpoint,
    agentName: args.agentName,
    definition,
    metadata,
  });
}
